// @alamo/ui · ThemeThumbnail
//
// Inline-SVG mini layout mock for a theme mode, Jira-style: rounded canvas with
// a header bar, a couple of neutral content lines, and one brand accent stroke.
// - light: white canvas, light-grey lines, laranja accent.
// - dark:  neutral charcoal canvas (#191A1C bg / #26282B card) — matches the
//          neutral dark palette (NOT saturated navy) — light lines.
// - auto:  diagonal split (light half / dark half) to signal "follows system".

import type { ThemeMode } from '@alamo/types';

import { cn } from '../../lib/cn';

export interface ThemeThumbnailProps {
  mode: ThemeMode;
  className?: string;
}

const ACCENT = '#EE781F'; // laranja-encanto

interface Palette {
  canvas: string;
  card: string;
  header: string;
  line: string;
  border: string;
}

const LIGHT: Palette = {
  canvas: '#FFFFFF',
  card: '#F1F3F5',
  header: '#E2E6EA',
  line: '#D5DBE1',
  border: '#E2E6EA',
};

const DARK: Palette = {
  canvas: '#191A1C',
  card: '#26282B',
  header: '#202123',
  line: '#3A3E44',
  border: '#2C2F33',
};

/** The layout content (header + lines + accent) drawn over a given palette. */
function Layout({ p }: { p: Palette }) {
  return (
    <>
      {/* header bar */}
      <rect x="6" y="6" width="52" height="7" rx="2" fill={p.header} />
      {/* accent stroke (brand) */}
      <rect x="6" y="19" width="18" height="4" rx="2" fill={ACCENT} />
      {/* content lines */}
      <rect x="6" y="27" width="52" height="3.2" rx="1.6" fill={p.line} />
      <rect x="6" y="33" width="44" height="3.2" rx="1.6" fill={p.line} />
      {/* card block */}
      <rect x="6" y="40" width="52" height="10" rx="2.5" fill={p.card} />
    </>
  );
}

/**
 * ThemeThumbnail — 64×56 rounded SVG mock of the layout in the given theme.
 * Decorative: `aria-hidden` (the ThemePicker card owns the accessible name).
 */
export function ThemeThumbnail({ mode, className }: ThemeThumbnailProps) {
  const clipId = `theme-thumb-clip-${mode}`;
  return (
    <svg
      viewBox="0 0 64 56"
      role="img"
      aria-hidden
      preserveAspectRatio="xMidYMid meet"
      className={cn('block aspect-[64/56] w-full', className)}
    >
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="0" width="64" height="56" rx="6" />
        </clipPath>
        <clipPath id={`${clipId}-diagonal`}>
          <polygon points="64,0 64,56 0,56" />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipId})`}>
        {mode === 'auto' ? (
          <>
            {/* light half */}
            <rect x="0" y="0" width="64" height="56" fill={LIGHT.canvas} />
            <Layout p={LIGHT} />
            {/* dark half clipped to a diagonal, drawn on top */}
            <g clipPath={`url(#${clipId}-diagonal)`}>
              <rect x="0" y="0" width="64" height="56" fill={DARK.canvas} />
              <Layout p={DARK} />
            </g>
            {/* diagonal seam */}
            <line x1="0" y1="56" x2="64" y2="0" stroke="#94A3B8" strokeWidth="0.75" />
          </>
        ) : (
          <>
            <rect
              x="0"
              y="0"
              width="64"
              height="56"
              fill={mode === 'dark' ? DARK.canvas : LIGHT.canvas}
            />
            <Layout p={mode === 'dark' ? DARK : LIGHT} />
          </>
        )}
      </g>

      {/* crisp rounded frame */}
      <rect
        x="0.5"
        y="0.5"
        width="63"
        height="55"
        rx="5.5"
        fill="none"
        stroke={mode === 'dark' ? DARK.border : LIGHT.border}
        strokeWidth="1"
      />
    </svg>
  );
}
